/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { CartModel } from '@/cart/data/models';
import { getPersistedCartData } from '@/cart/lib/persisted-data';
import { events } from '@adobe-commerce/event-bus';
import { MiniCart as MiniCartComponent } from '@/cart/components';
import { CartSummaryList } from '@/cart/containers';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button, ImageProps, Price } from '@adobe-commerce/elsie/components';
import { updateProductsFromCart } from '@/cart/api';
import { publishInitiateCheckoutEvent } from '@/cart/lib/acdl';
import { state } from '@/cart/lib/state';

// Delay navigation to allow analytics event to be sent before page unload.
// 100ms provides a reasonable balance between user experience and event reliability.
const ANALYTICS_NAVIGATION_DELAY_MS = 100;

export interface MiniCartProps extends HTMLAttributes<HTMLDivElement> {
  routeProduct?: (item: CartModel['items'][0]) => string;
  routeCart?: () => string;
  routeCheckout?: () => string;
  routeEmptyCartCTA?: () => string;
  slots?: {
    ProductList?: SlotProps;
    ProductListFooter?: SlotProps;
    PreCheckoutSection?: SlotProps;
    Thumbnail?: SlotProps<{
      item: CartModel['items'][number];
      defaultImageProps: ImageProps;
    }>;
    Heading?: SlotProps;
    EmptyCart?: SlotProps;
    Footer?: SlotProps;
    RowTotalFooter?: SlotProps<{ item: CartModel['items'][number] }>;
    ProductAttributes?: SlotProps;
    CartSummaryFooter?: SlotProps;
    CartItem?: SlotProps; // For backward compatibility
    UndoBanner?: SlotProps<{
      item: CartModel['items'][0];
      loading: boolean;
      error?: string;
      onUndo: () => void;
      onDismiss: () => void;
    }>;
    ConfirmDeleteBanner?: SlotProps<{
      item: CartModel['items'][0];
      loading: boolean;
      onConfirm: () => void;
      onCancel: () => void;
    }>;
    ItemTitle?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemPrice?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemQuantity?: SlotProps<{
      item: CartModel['items'][number];
      enableUpdateItemQuantity: boolean | { removeOnZero?: boolean };
      handleItemQuantityUpdate: (
        item: CartModel['items'][number],
        quantity: number
      ) => void;
      itemsLoading: Set<string>;
      handleItemsError: (uid: string, message?: string) => void;
      handleItemsLoading: (uid: string, state: boolean) => void;
      onItemUpdate?: ({ item }: { item: CartModel['items'][number] }) => void;
    }>;
    ItemTotal?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemSku?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemRemoveAction?: SlotProps<{
      item: CartModel['items'][number];
      enableRemoveItem: boolean;
      handleItemQuantityUpdate: (
        item: CartModel['items'][number],
        quantity: number
      ) => void;
      handleItemsError: (uid: string, message?: string) => void;
      handleItemsLoading: (uid: string, state: boolean) => void;
      onItemUpdate?: ({ item }: { item: CartModel['items'][number] }) => void;
      itemsLoading: Set<string>;
    }>;
  };
  hideFooter?: boolean;
  displayAllItems?: boolean;
  showDiscount?: boolean;
  showSavings?: boolean;
  enableItemRemoval?: boolean;
  enableQuantityUpdate?: boolean | { removeOnZero?: boolean };
  hideHeading?: boolean;
  undo?: boolean;
  confirmBeforeDelete?: boolean;
}

export const MiniCart: Container<MiniCartProps, CartModel | null> = ({
  children,
  initialData = null,
  hideFooter = true, // Default to true for MiniCart
  slots,
  routeProduct,
  routeCart,
  routeCheckout,
  routeEmptyCartCTA,
  showDiscount,
  showSavings,
  enableItemRemoval = true, // Default to true for MiniCart
  enableQuantityUpdate = false, // Default to false for MiniCart
  hideHeading = false, // Default to false for MiniCart
  undo = false,
  confirmBeforeDelete = false,
  ...props
}) => {
  const [data, setData] = useState<CartModel | null>(initialData);
  const cartTaxesConfig = state.config?.shoppingCartDisplaySetting;

  useEffect(() => {
    const event = events.on(
      'cart/data',
      (payload) => {
        setData(payload as CartModel);
      },
      { eager: true }
    );

    return () => {
      event?.off();
    };
  }, []);

  const dictionary = useText({
    cartLink: 'Cart.MiniCart.cartLink',
    checkoutLink: 'Cart.MiniCart.checkoutLink',
  });

  const handleItemQuantityUpdate = (uid: string, quantity: number) => {
    return updateProductsFromCart([{ uid, quantity }]);
  };

  const handleItemRemove = (uid: string) => handleItemQuantityUpdate(uid, 0);

  const checkoutLinkDisabled = data?.hasOutOfStockItems;

  const handleInitiateCheckout = useCallback(
    (e: MouseEvent) => {
      if (data && !checkoutLinkDisabled) {
        e.preventDefault();
        publishInitiateCheckoutEvent(data, state.locale);

        setTimeout(() => {
          const checkoutUrl = routeCheckout?.();
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
          }
        }, ANALYTICS_NAVIGATION_DELAY_MS);
      }
    },
    [data, checkoutLinkDisabled, routeCheckout]
  );

  const productList = (
    <Slot
      name="ProductList"
      slot={slots?.ProductList}
      context={{
        itemQuantityUpdateHandler: handleItemQuantityUpdate,
        itemRemoveHandler: handleItemRemove,
        totalQuantity: data?.totalQuantity,
      }}
    >
      <CartSummaryList
        data-testid="default-cart-summary-list"
        routeProduct={routeProduct}
        routeEmptyCartCTA={routeEmptyCartCTA}
        initialData={data}
        maxItems={state.config?.miniCartMaxItemsDisplay}
        showMaxItems={!!state.config?.miniCartMaxItemsDisplay}
        hideHeading={hideHeading || !data?.totalQuantity}
        headingLevel={2}
        itemTitleHeadingLevel={3}
        hideFooter={hideFooter}
        enableRemoveItem={enableItemRemoval}
        enableUpdateItemQuantity={enableQuantityUpdate}
        showDiscount={showDiscount}
        showSavings={showSavings}
        undo={undo}
        confirmBeforeDelete={confirmBeforeDelete}
        slots={{
          Thumbnail: slots?.Thumbnail,
          Heading: slots?.Heading,
          EmptyCart: slots?.EmptyCart,
          Footer: slots?.Footer,
          RowTotalFooter: slots?.RowTotalFooter,
          ProductAttributes: slots?.ProductAttributes,
          CartSummaryFooter: slots?.CartSummaryFooter,
          CartItem: slots?.CartItem, // Keep for backward compatibility
          UndoBanner: slots?.UndoBanner,
          ConfirmDeleteBanner: slots?.ConfirmDeleteBanner,
          ItemTitle: slots?.ItemTitle,
          ItemPrice: slots?.ItemPrice,
          ItemQuantity: slots?.ItemQuantity,
          ItemTotal: slots?.ItemTotal,
          ItemSku: slots?.ItemSku,
          ItemRemoveAction: slots?.ItemRemoveAction,
        }}
      />
    </Slot>
  );

  const getProductListFooter = (data: CartModel | null) => {
    return (
      <Slot
        name="ProductListFooter"
        slot={slots?.ProductListFooter}
        context={{
          data,
        }}
      />
    );
  };

  const getPreCheckoutSection = (data: CartModel | null) => {
    return (
      <Slot
        name="PreCheckoutSection"
        slot={slots?.PreCheckoutSection}
        context={{
          data,
        }}
      />
    );
  };

  const getSubtotalPriceProps = () =>
    cartTaxesConfig?.subtotal === 'INCLUDING_TAX' ||
    cartTaxesConfig?.subtotal === 'INCLUDING_EXCLUDING_TAX'
      ? {
          amount: data?.subtotal.includingTax.value,
          currency: data?.subtotal.includingTax.currency,
          'data-testid': 'subtotal-including-tax',
          style: { font: 'inherit' },
        }
      : {
          amount: data?.subtotal.excludingTax.value,
          currency: data?.subtotal.excludingTax.currency,
          'data-testid': 'subtotal-excluding-tax',
          style: { font: 'inherit' },
        };

  return (
    <MiniCartComponent
      {...props}
      productListFooter={getProductListFooter(data)}
      subtotal={
        !data?.totalQuantity
          ? undefined
          : data?.subtotal && <Price {...getSubtotalPriceProps()} />
      }
      subtotalExcludingTaxes={
        !data?.totalQuantity
          ? undefined
          : data?.subtotal &&
            (cartTaxesConfig?.subtotal === 'INCLUDING_EXCLUDING_TAX' ? (
              <Price
                amount={data?.subtotal.excludingTax.value}
                currency={data?.subtotal.excludingTax.currency}
                data-testid="subtotal-including-excluding-tax"
                style={{ font: 'inherit' }}
              />
            ) : undefined)
      }
      preCheckoutSection={getPreCheckoutSection(data)}
      ctas={
        !data?.totalQuantity ? undefined : (
          <div>
            {routeCheckout && (
              <Button
                data-testid="route-checkout-button"
                variant="primary"
                href={checkoutLinkDisabled ? undefined : routeCheckout()}
                disabled={checkoutLinkDisabled}
                aria-disabled={checkoutLinkDisabled}
                onClick={handleInitiateCheckout}
              >
                {dictionary.checkoutLink}
              </Button>
            )}
            {routeCart && (
              <Button
                data-testid="route-cart-button"
                variant="tertiary"
                href={routeCart()}
              >
                {dictionary.cartLink}
              </Button>
            )}
          </div>
        )
      }
      products={productList}
    />
  );
};

MiniCart.getInitialData = async function () {
  return getPersistedCartData();
};
